import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { presetRange, customRange, translatedPresets } from '../../constants/datePresets';
import { usePhiSearchTerm } from '../../lib/phiSearch';

// ─────────────────────────────────────────────────────────────────────────────
// سوابق ویزیت‌ها — the one behavioural truth behind both surfaces.
//
// DESIGN-CONCEPT-MOBILE v2 §1.1: the desktop table and the phone card list are
// independent RENDER trees over a shared logic layer, never one responsive
// component with variant props. This module is that layer — the vocabularies,
// the column/label definition, the URL-is-state contract and the query window —
// so «انجام‌شده» means the same thing on both, the same filters survive a
// refresh, and a link shared from a phone opens the same view on a desktop.
//
// Extracted from Encounters.web.jsx UNCHANGED when the mobile screen was built:
// the desktop's behaviour is the reference, and a rewrite would have been a
// second opinion about what a filter means.
// ─────────────────────────────────────────────────────────────────────────────

export { PAGE_SIZE } from '../../constants/pagination';

export const CLINICS_QUERY = `
  query ListMyClinics {
    listClinics(limit: 50) {
      clinics { id name }
    }
  }
`;

// Same presets as the accounting page, minus 'همه' — a dated visit history has
// no unbounded default (and null bounds mean "today" at the BFF, so 'همه'
// cannot be null).
export const datePresetsFor = (t) => translatedPresets(t).filter(p => p.id !== 'all');

export const TYPE_CFG = {
  'in-person': 'encounters.type.inPerson',
  'online':    'encounters.type.online',
};

// Encounter status → { key: canonical token colour, labelKey: history wording }.
// The colour comes from the one status source (StatusChip/statusColors); the
// label stays in the encounter-history vocabulary, which differs from the
// appointment desk's ("انجام شده" not "ویزیت‌شده"). Legacy aliases tolerate
// raw/old statuses.
export const ENC_STATUS = {
  planned:     { key: 'scheduled', labelKey: 'encounters.status.scheduled' },
  scheduled:   { key: 'scheduled', labelKey: 'encounters.status.scheduled' },
  arrived:     { key: 'arrived',   labelKey: 'encounters.status.arrived' },
  in_progress: { key: 'in_visit',  labelKey: 'encounters.status.inVisit' },
  in_visit:    { key: 'in_visit',  labelKey: 'encounters.status.inVisit' },
  finished:    { key: 'completed', labelKey: 'encounters.status.completed' },
  completed:   { key: 'completed', labelKey: 'encounters.status.completed' },
  cancelled:   { key: 'cancelled', labelKey: 'encounters.status.cancelled' },
};

export const FILTERS = [
  { id: 'all',         labelKey: 'common.state.all' },
  { id: 'scheduled',   labelKey: 'encounters.status.scheduled' },
  { id: 'in_progress', labelKey: 'encounters.status.inVisit' },
  { id: 'completed',   labelKey: 'encounters.status.completed' },
];

export const TYPE_OPTIONS = [
  { value: 'in-person', labelKey: 'encounters.type.inPerson' },
  { value: 'online',    labelKey: 'encounters.type.online' },
];

// ── The one column/label definition (§5.1: table and card render from it) ────
export const COLUMNS = [
  { id: 'patient',    labelKey: 'common.labels.patient' },
  { id: 'fileNumber', labelKey: 'encounters.columns.fileNumber' },
  { id: 'type',       labelKey: 'encounters.columns.type' },
  { id: 'date',       labelKey: 'encounters.columns.date' },
  { id: 'clinic',     labelKey: 'common.labels.clinic' },
  { id: 'status',     labelKey: 'common.labels.status' },
];

export const colLabel = (t, id) => {
  const col = COLUMNS.find(c => c.id === id);
  return col ? t(col.labelKey) : '';
};

// `i18n` is the `useTranslation()` value. Passing it in keeps this a pure
// lookup that the table, the desktop card and the phone card all share.
export const cellValue = (col, e, clinicName, { t, formatDate, formatDigits }) => {
  switch (col) {
    // A file number is an identifier, not a quantity: localise the digits but
    // never group them, or «۱۲۳۴» reads as a different file.
    case 'fileNumber': return e.fileNumber ? formatDigits(e.fileNumber) : '—';
    case 'type':       return TYPE_CFG[e.type] ? t(TYPE_CFG[e.type]) : (e.type || '—');
    case 'date':       return formatDate(e.scheduledDate || e.createdAt, 'long');
    case 'clinic':     return clinicName || '—';
    default:           return '—';
  }
};

// ── The URL-is-state contract (§3.2) ─────────────────────────────────────────
// A param at its default is REMOVED from the URL, so a shared link carries only
// what was actually chosen and «امروز» never appears as noise.
//
// `view` is desktop-only — the phone renders cards and nothing else — but it
// stays in DEFAULTS so a link made on a desktop keeps its meaning when it
// round-trips through a phone.
export const DEFAULTS = {
  status: 'all', type: '', clinic: '', q: '',
  view: 'card', preset: 'today', from: '', to: '',
};

/**
 * The filter state, read from and written to the URL.
 *
 * Both surfaces call this and neither owns a copy: refresh, back/forward and a
 * shared link all restore the exact view, on either of them.
 */
export function useEncounterFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The search term is the one filter NOT in the URL: it can be a patient's
  // name (§3.2). It lives in the tab's session instead — see lib/phiSearch.
  const [query, setQuery] = usePhiSearchTerm('encounters');

  const param = (k) => searchParams.get(k) ?? DEFAULTS[k];
  const filter   = param('status');
  const type     = param('type');
  const clinicId = param('clinic');
  const preset   = param('preset');
  const custom   = { from: param('from'), to: param('to') };
  const page     = Math.max(0, (parseInt(searchParams.get('page') || '1', 10) || 1) - 1);

  const setParams = React.useCallback((patch, { resetPage = true, replace = false } = {}) => {
    const { q, ...urlPatch } = patch;
    if (q !== undefined) setQuery(q);
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(urlPatch)) {
      if (v == null || v === '' || v === DEFAULTS[k]) next.delete(k); else next.set(k, v);
    }
    next.delete('q'); // never written back, even by an old caller
    if (resetPage) next.delete('page');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace });
  }, [searchParams, setSearchParams, setQuery]);

  // Preset/custom → epoch-ms window. A half-picked custom range holds the
  // previous window (null,null would collapse to "today" at the BFF), so the
  // list never flickers to today mid-edit.
  const { fromTs, toTs } = React.useMemo(() => {
    if (preset === 'custom') {
      if (!custom.from || !custom.to) return { fromTs: undefined, toTs: undefined };
      return customRange(custom.from, custom.to);
    }
    return presetRange(preset) || { fromTs: undefined, toTs: undefined };
  }, [preset, custom.from, custom.to]);

  // Any non-default filter, the date window included — its default is «امروز»,
  // which is what makes the unfiltered empty state able to speak about today.
  const hasActiveFilters =
    filter !== DEFAULTS.status || type !== DEFAULTS.type || clinicId !== DEFAULTS.clinic
    || query !== DEFAULTS.q || preset !== DEFAULTS.preset;

  const clearFilters = React.useCallback(
    () => setParams({ status: '', type: '', clinic: '', q: '', preset: '', from: '', to: '' }),
    [setParams],
  );

  return {
    searchParams, setParams,
    filter, type, clinicId, query, preset, custom, page,
    fromTs, toTs, hasActiveFilters, clearFilters,
    view: param('view'),
  };
}

/**
 * The clinic filter's options.
 *
 * A loader, not a data region: on failure the filter simply does not render.
 * The list itself has its own error branch.
 */
export function useClinicOptions() {
  const [clinics, setClinics] = React.useState([]);

  React.useEffect(() => {
    bffQuery(CLINICS_QUERY)
      .then(d => setClinics(d?.listClinics?.clinics || []))
      .catch(() => {});
  }, []);

  const clinicNameById = React.useMemo(
    () => Object.fromEntries(clinics.map(c => [c.id, c.name])),
    [clinics],
  );

  return { clinics, clinicNameById };
}

/**
 * The debounced search box's behaviour, identical on both surfaces.
 *
 * Minimum 2 characters before a server query (§8); clearing commits
 * immediately. `replace` keeps typing out of the history stack — otherwise
 * every keystroke would be a back-button step.
 */
export function useEncounterSearch(query, setParams) {
  const [search, setSearch] = React.useState(query);
  const debounceRef = React.useRef(null);
  // What THIS box last committed. Only a change from elsewhere («حذف فیلترها», a
  // migrated old link) resyncs the box — committing '' for a 1-letter entry must
  // not erase the letter the doctor just typed.
  const committedRef = React.useRef(query);
  React.useEffect(() => {
    if (query !== committedRef.current) { committedRef.current = query; setSearch(query); }
  }, [query]);
  React.useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    if (val === '') { committedRef.current = ''; setParams({ q: '' }, { replace: true }); return; }
    // Below two characters the PREVIOUS term is dropped too — otherwise the list
    // kept filtering by «ab» while the box showed «a» (§11.3).
    const commit = val.trim().length < 2 ? '' : val;
    debounceRef.current = setTimeout(() => {
      committedRef.current = commit;
      setParams({ q: commit }, { replace: true });
    }, 300);
  };

  return { search, onSearch };
}
